import type { IPayPeriodTemplate } from "@/business/business";
import {
  PAY_PERIOD_TEMPLATE_CADENCE,
  PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK,
} from "@/business/constants";
import { dayjs } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import type { EnrichedIcmStatement, IcmJob, IcmPeriod } from "@/icm/types";
import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import type { IPayPeriod, PayrollRunStatus } from "@/payroll/payroll.d";
import { isPayPeriodLocked } from "@/payroll/utils/period-helpers";
import { generatePayrollPeriods } from "@/spiffs/payroll-periods/utils/payroll-period-utils";

export const DEFAULT_TEMPLATE: IPayPeriodTemplate = {
  cadence: PAY_PERIOD_TEMPLATE_CADENCE.weekly,
  start_day_of_week: PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.monday,
};

export const getSummaryMetrics = (
  statements: EnrichedIcmStatement[] | undefined,
  jobs: IcmJob[],
) => {
  const totalPayout =
    statements?.reduce(
      (sum, statement) => sum + parseFloat(statement.total_payout),
      0,
    ) ?? 0;

  const totalRevenue = jobs.reduce((sum, job) => sum + (job.job_total ?? 0), 0);
  const commissionRate =
    totalRevenue > 0 ? (totalPayout / totalRevenue) * 100 : null;
  const uniqueJobCount = new Set(
    jobs.map((job) => job.job_id).filter((id) => id !== null),
  ).size;

  return {
    totalPayout,
    totalRevenue,
    totalRevenueFormatted:
      formatMoney(totalRevenue.toFixed(2)) ?? formatMoney("0") ?? "$0.00",
    commissionRate,
    uniqueJobCount,
  };
};

export const buildPeriods = ({
  template,
  payPeriods,
  isBusinessLoading,
  businessCreatedAt,
}: {
  template: IPayPeriodTemplate;
  payPeriods: Array<{
    start_date: string;
    end_date: string;
    icm_runs_count: number;
    latest_icm_run_status?: PayrollRunStatus | null;
    status?: IPayPeriod["status"];
  }>;
  isBusinessLoading: boolean;
  businessCreatedAt?: string | null;
}) => {
  if (isBusinessLoading) return { periods: [], calendarPeriods: [] };

  const existingPeriods: IcmPeriod[] = payPeriods.map((period) => ({
    period_start: period.start_date,
    period_end: period.end_date,
    has_payroll_run: period.icm_runs_count > 0,
    latest_icm_run_status: period.latest_icm_run_status ?? null,
    is_locked: isPayPeriodLocked(period),
  }));

  const toIcmPeriod = (period: {
    start: { format: (format: string) => string };
    end: { format: (format: string) => string };
  }): IcmPeriod => {
    const start = period.start.format("YYYY-MM-DD");
    const end = period.end.format("YYYY-MM-DD");

    return {
      period_start: start,
      period_end: end,
      has_payroll_run: false,
      latest_icm_run_status: null,
      is_locked: false,
    };
  };

  const businessCreatedAtDate = businessCreatedAt
    ? dayjs(businessCreatedAt)
    : null;

  const periodFilter = (period: IcmPeriod) =>
    !businessCreatedAtDate ||
    !dayjs(period.period_start).isBefore(businessCreatedAtDate, "day");

  const mergePeriods = (generatedPeriods: IcmPeriod[]) => {
    const periodMap = new Map(
      existingPeriods.map((period) => [period.period_start, period]),
    );

    generatedPeriods.filter(periodFilter).forEach((period) => {
      if (!periodMap.has(period.period_start)) {
        periodMap.set(period.period_start, period);
      }
    });

    return Array.from(periodMap.values()).sort(
      (a, b) =>
        dayjs(b.period_start).valueOf() - dayjs(a.period_start).valueOf(),
    );
  };

  const periods = mergePeriods(
    [...generatePayrollPeriods(template, 52, 0)].reverse().map(toIcmPeriod),
  );

  const calendarPeriods = mergePeriods(
    [...generatePayrollPeriods(template, 260, 0)].reverse().map(toIcmPeriod),
  );

  return { periods, calendarPeriods };
};

export const hasActiveIcmRun = (
  period?: { latest_icm_run_status?: string | null } | null,
) =>
  period?.latest_icm_run_status === PAYROLL_RUN_STATUS.pending ||
  period?.latest_icm_run_status === PAYROLL_RUN_STATUS.processing;
